import * as React from "react"
import { ImageUp, Paintbrush } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LABEL_MM, type BottleType } from "@/lib/label-canvas/schema"
import {
  TEMPLATE_OCCASIONS,
  TEMPLATE_SLOT_LABELS,
  renderTemplateThumbnail,
  templatesFor,
  type LabelTemplate,
  type TemplateFilter,
} from "@/lib/label-canvas/templates"
import { cn } from "@/lib/utils"
import { StudioSheet } from "./StudioSheet"

// The template gallery (W5, W6): two ways to start (a blank label, or
// finished artwork) plus, when our studio can paint (M5), a third
// ("Describe your design"), then the twenty designs filtered by occasion, or by
// "Logo" for the ten built around a logo slot. Each tile is a live thumbnail
// drawn by the same renderer as the editor, for the bottle being designed, so
// there are no static images to keep in step with the templates; a design
// with an image slot carries a badge naming it. The editor decides what
// choosing a tile does (apply, or confirm first).

const THUMB_PX = 220
const FILTER_CHIPS: readonly { key: TemplateFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "logo", label: "Logo" },
  ...TEMPLATE_OCCASIONS,
]

type ThumbLoader = (template: LabelTemplate, bottle: BottleType) => Promise<HTMLCanvasElement>

/**
 * One tile's thumbnail: drawn once from the cached render. Until it lands the
 * canvas shows as a warm-white block of the label's shape (no spinner).
 */
function TemplateThumb({ template, bottle, load }: { template: LabelTemplate; bottle: BottleType; load: ThumbLoader }) {
  const ref = React.useRef<HTMLCanvasElement>(null)
  const [ready, setReady] = React.useState(false)
  React.useEffect(() => {
    let live = true
    setReady(false)
    load(template, bottle)
      .then((source) => {
        const canvas = ref.current
        if (!live || !canvas) return
        canvas.width = source.width
        canvas.height = source.height
        canvas.getContext("2d")?.drawImage(source, 0, 0)
        setReady(true)
      })
      .catch((error) => console.error("[studio] template thumbnail failed", template.key, error))
    return () => {
      live = false
    }
  }, [template, bottle, load])
  const mm = LABEL_MM[bottle]
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={cn("studio-gallery-thumb", ready && "is-ready")}
      style={{ aspectRatio: `${mm.width} / ${mm.height}` }}
    />
  )
}

export function TemplateGallery({
  open,
  onOpenChange,
  bottle,
  onBlank,
  onOwnArtwork,
  onDescribe,
  onChoose,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  bottle: BottleType
  onBlank: () => void
  onOwnArtwork: () => void
  /** "Describe your design" (M5); the tile shows only when this is given (`ai.enabled`). */
  onDescribe?: () => void
  onChoose: (template: LabelTemplate) => void
}) {
  const [filter, setFilter] = React.useState<TemplateFilter>("all")

  // Rendered thumbnails, per template × bottle, kept while the studio is open
  // (this component stays mounted; only the sheet's content comes and goes).
  const cache = React.useRef(new Map<string, Promise<HTMLCanvasElement>>())
  const load = React.useCallback<ThumbLoader>((template, forBottle) => {
    const key = `${template.key}|${forBottle}`
    let render = cache.current.get(key)
    if (!render) {
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      render = renderTemplateThumbnail(template, forBottle, THUMB_PX, ratio)
      render.catch(() => cache.current.delete(key))
      cache.current.set(key, render)
    }
    return render
  }, [])

  return (
    <StudioSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Templates"
      description="Start from a design and make it yours — every element stays editable."
      size="wide"
    >
      <div className="studio-gallery">
        <div
          className={cn("studio-gallery-start", onDescribe && "has-describe")}
          role="group"
          aria-label="Start from"
        >
          <button type="button" className="studio-gallery-special" onClick={onBlank}>
            <span className="studio-gallery-blank" aria-hidden="true" />
            <span className="studio-gallery-special-text">
              <span className="studio-gallery-name">Blank label</span>
              <span className="studio-gallery-desc">Plain warm-white paper, built element by element.</span>
            </span>
          </button>
          <button type="button" className="studio-gallery-special" onClick={onOwnArtwork}>
            <span className="studio-gallery-artwork" aria-hidden="true">
              <ImageUp />
            </span>
            <span className="studio-gallery-special-text">
              <span className="studio-gallery-name">I have my own artwork</span>
              <span className="studio-gallery-desc">A finished design from your graphic designer, edge to edge.</span>
            </span>
          </button>
          {onDescribe && (
            <button type="button" className="studio-gallery-special studio-gallery-special-describe" onClick={onDescribe}>
              <span className="studio-gallery-artwork studio-gallery-describe" aria-hidden="true">
                <Paintbrush />
              </span>
              <span className="studio-gallery-special-text">
                <span className="studio-gallery-name">Describe your design</span>
                <span className="studio-gallery-desc">Tell us the feeling; our studio paints two artworks to choose from.</span>
              </span>
            </button>
          )}
        </div>

        <div className="studio-chips studio-chips-scroll studio-gallery-chips" role="group" aria-label="Occasion">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className="studio-chip"
              aria-pressed={filter === chip.key}
              onClick={() => setFilter(chip.key)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className="studio-gallery-grid">
          {templatesFor(filter).map((template) => (
            <button key={template.key} type="button" className="studio-gallery-tile" onClick={() => onChoose(template)}>
              <span className="studio-gallery-mat">
                <TemplateThumb template={template} bottle={bottle} load={load} />
              </span>
              <span className="studio-gallery-name">{template.name}</span>
              {template.slot && <span className="studio-gallery-badge">{TEMPLATE_SLOT_LABELS[template.slot]}</span>}
              <span className="studio-gallery-desc">{template.description}</span>
            </button>
          ))}
        </div>
      </div>
    </StudioSheet>
  )
}

/** "I have my own artwork": the print spec, then the picker. */
export function OwnArtworkSheet({
  open,
  onOpenChange,
  onChoose,
  replaces,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onChoose: () => void
  /** The label already has elements: placing the artwork replaces them. */
  replaces: boolean
}) {
  return (
    <StudioSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Add your finished artwork"
      description="Your designer’s finished label fills the front label edge to edge, ready to see on the bottle."
      size="narrow"
    >
      <div className="studio-panel studio-artwork">
        <div className="field-label">Artwork specification</div>
        <div className="field-hint">
          PNG or JPEG · 300 dpi · wine 90 × 100 mm, MCC 80 × 100 mm · plus 3 mm bleed on every side · we check every
          design before print.
        </div>
        <div className="field-hint">Every mock-up is subject to graphic design approval by our team.</div>
        <div className="field-hint">PDF? Send it with your enquiry instead.</div>
        {replaces && <div className="field-hint">This replaces your current label. Undo brings it back.</div>}
        <Button type="button" variant="gold" size="lg" onClick={onChoose}>
          <ImageUp className="mr-2 h-4 w-4" aria-hidden="true" />
          Choose file
        </Button>
      </div>
    </StudioSheet>
  )
}
